<template>
  <div id="app">
    <router-view />
  </div>
</template>

<script>
export default {
  name: 'App'
}
</script>

<style>
:root {
  /* 主色 - 学术蓝 */
  --primary-color: #1E5BB8;
  --primary-color-light: #2E75D9;
  --primary-color-dark: #0F3D85;
  --primary-50: #EEF4FC;
  --primary-100: #DCE8F8;
  --primary-500: #1E5BB8;
  --primary-600: #1A4FA0;
  --primary-700: #0F3D85;

  /* 渐变 */
  --gradient-primary: linear-gradient(135deg, #1E5BB8 0%, #2E75D9 100%);
  --gradient-hero: linear-gradient(135deg, #0B2548 0%, #11366B 35%, #1E5BB8 100%);
  --gradient-accent: linear-gradient(135deg, #1E5BB8 0%, #0EA5E9 100%);

  /* 中性色 */
  --bg-base: #F5F7FB;
  --bg-light: #FFFFFF;
  --bg-soft: #FAFBFD;
  --bg-dark: #EAEEF6;

  --heading-color: #0F1B33;
  --text-color-primary: #1A2540;
  --text-color-secondary: #5A6B85;
  --text-color-muted: #8C99B0;
  --text-color-disabled: #B6BDCC;

  --border-color-base: #E5EAF2;
  --border-color-light: #EEF1F7;
  --border-color-strong: #D7DEEB;

  /* 功能色 */
  --success-color: #10B981;
  --success-bg: #ECFDF5;
  --warning-color: #F59E0B;
  --warning-bg: #FFFBEB;
  --danger-color: #EF4444;
  --danger-bg: #FEF2F2;
  --info-color: #0EA5E9;
  --info-bg: #ECFEFF;

  /* 阴影 */
  --shadow-xs: 0 1px 2px rgba(15, 27, 51, 0.04);
  --shadow-sm: 0 2px 6px rgba(15, 27, 51, 0.05);
  --shadow-md: 0 4px 14px rgba(15, 27, 51, 0.06), 0 1px 3px rgba(15, 27, 51, 0.04);
  --shadow-lg: 0 12px 32px rgba(15, 27, 51, 0.08), 0 2px 6px rgba(15, 27, 51, 0.04);
  --shadow-xl: 0 20px 40px rgba(15, 27, 51, 0.12), 0 4px 10px rgba(15, 27, 51, 0.04);

  /* 圆角 */
  --radius-sm: 6px;
  --radius-md: 10px;
  --radius-lg: 14px;
  --radius-xl: 20px;

  /* 过渡 */
  --transition-base: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  --transition-fast: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  --transition-slow: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);

  /* 兼容旧变量 */
  --component-background: #FFFFFF;
  --box-shadow-base: var(--shadow-sm);
  --box-shadow-card: var(--shadow-md);
  --box-shadow-hover: var(--shadow-lg);
  --border-radius-base: var(--radius-sm);
  --border-radius-medium: var(--radius-md);
  --border-radius-large: var(--radius-lg);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC',
    'Hiragino Sans GB', 'Microsoft YaHei', '微软雅黑', 'Helvetica Neue',
    Helvetica, Arial, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-color-primary);
  background-color: var(--bg-base);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

#app {
  height: 100%;
}

/* 自定义滚动条 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #D7DEEB;
  border-radius: 4px;
  transition: background 0.2s;
}

::-webkit-scrollbar-thumb:hover {
  background: #B6BDCC;
}

/* 通用卡片 */
.card {
  background-color: var(--bg-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-color-light);
  transition: var(--transition-base);
  overflow: hidden;
  margin-bottom: 24px;
  padding: 28px;
}

.card:hover {
  box-shadow: var(--shadow-lg);
  border-color: var(--border-color-base);
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--heading-color);
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border-color-light);
  letter-spacing: -0.2px;
}

/* ============ Element UI 样式覆盖 ============ */

/* 按钮 - 主按钮使用品牌渐变 */
.el-button {
  border-radius: var(--radius-sm) !important;
  font-weight: 500 !important;
  transition: var(--transition-base) !important;
  letter-spacing: 0.2px;
}

.el-button--primary {
  background: var(--gradient-primary) !important;
  border-color: var(--primary-color) !important;
  box-shadow: 0 2px 6px rgba(30, 91, 184, 0.18);
}

.el-button--primary:hover,
.el-button--primary:focus {
  background: linear-gradient(135deg, #2E75D9 0%, #4493EE 100%) !important;
  border-color: var(--primary-color-light) !important;
  box-shadow: 0 6px 18px rgba(30, 91, 184, 0.28);
  transform: translateY(-1px);
}

.el-button--primary:active {
  transform: translateY(0);
}

.el-button--success {
  background: linear-gradient(135deg, #10B981 0%, #34D399 100%) !important;
  border-color: #10B981 !important;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.18);
}

.el-button--success:hover,
.el-button--success:focus {
  background: linear-gradient(135deg, #34D399 0%, #6EE7B7 100%) !important;
  border-color: #34D399 !important;
  box-shadow: 0 6px 18px rgba(16, 185, 129, 0.28);
  transform: translateY(-1px);
}

.el-button--warning {
  background: linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%) !important;
  border-color: #F59E0B !important;
  box-shadow: 0 2px 6px rgba(245, 158, 11, 0.18);
}

.el-button--warning:hover,
.el-button--warning:focus {
  background: linear-gradient(135deg, #FBBF24 0%, #FCD34D 100%) !important;
  border-color: #FBBF24 !important;
  transform: translateY(-1px);
}

.el-button--info {
  background: #6B7894 !important;
  border-color: #6B7894 !important;
}

.el-button--default {
  border-color: var(--border-color-strong) !important;
  color: var(--text-color-primary) !important;
  background-color: var(--bg-light) !important;
}

.el-button--default:hover,
.el-button--default:focus {
  border-color: var(--primary-color) !important;
  color: var(--primary-color) !important;
  background-color: var(--primary-50) !important;
  transform: translateY(-1px);
}

.el-button--text {
  color: var(--primary-color) !important;
  background: transparent !important;
  box-shadow: none !important;
}

.el-button--text:hover {
  color: var(--primary-color-light) !important;
  transform: none;
}

/* 输入框 */
.el-input__inner {
  height: 42px !important;
  line-height: 42px !important;
  border-radius: var(--radius-sm) !important;
  border: 1px solid var(--border-color-base) !important;
  transition: var(--transition-fast) !important;
}

.el-input__inner:hover {
  border-color: var(--border-color-strong) !important;
}

.el-input__inner:focus {
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 0 3px rgba(30, 91, 184, 0.1) !important;
}

.el-button {
  height: 40px !important;
  line-height: 40px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.el-button--small {
  height: 34px !important;
  line-height: 34px !important;
}

.el-button--mini {
  height: 28px !important;
  line-height: 28px !important;
}

/* 顶部导航 */
.el-menu--horizontal > .el-menu-item.is-active {
  color: var(--primary-color) !important;
  border-bottom-color: transparent !important;
}

.el-menu--horizontal > .el-menu-item {
  height: 64px !important;
  line-height: 64px !important;
}

.el-menu--horizontal.el-menu {
  border-bottom: none !important;
}

.el-menu--horizontal > .el-menu-item:not(.is-disabled):focus,
.el-menu--horizontal > .el-menu-item:not(.is-disabled):hover {
  background-color: rgba(30, 91, 184, 0.04) !important;
  color: var(--primary-color) !important;
}

/* 下拉菜单 */
.el-dropdown-menu {
  border-radius: var(--radius-md) !important;
  box-shadow: var(--shadow-lg) !important;
  padding: 6px 0 !important;
  border: 1px solid var(--border-color-light) !important;
}

.el-dropdown-menu__item {
  padding: 10px 20px !important;
  font-size: 14px !important;
  line-height: 1.5 !important;
  transition: var(--transition-fast) !important;
}

.el-dropdown-menu__item:not(.is-disabled):hover {
  background-color: var(--primary-50) !important;
  color: var(--primary-color) !important;
}

/* 分页 */
.el-pagination.is-background .el-pager li:not(.disabled).active {
  background: var(--gradient-primary) !important;
  color: #fff !important;
  box-shadow: 0 2px 6px rgba(30, 91, 184, 0.25);
}

.el-pagination.is-background .el-pager li:not(.disabled):hover {
  color: var(--primary-color) !important;
}

.el-pagination.is-background .el-pager li,
.el-pagination.is-background .btn-prev,
.el-pagination.is-background .btn-next {
  border-radius: var(--radius-sm) !important;
}

/* 标签 */
.el-tag {
  border-radius: var(--radius-sm) !important;
  font-weight: 500 !important;
}

/* 选择器 */
.el-select .el-input__inner:focus {
  border-color: var(--primary-color) !important;
}

.el-select-dropdown {
  border-radius: var(--radius-md) !important;
  box-shadow: var(--shadow-lg) !important;
}

.el-select-dropdown__item.selected {
  color: var(--primary-color) !important;
  font-weight: 500 !important;
}

/* 对话框 */
.el-dialog {
  border-radius: var(--radius-lg) !important;
  overflow: hidden;
  box-shadow: var(--shadow-xl) !important;
}

.el-dialog__header {
  padding: 22px 28px !important;
  border-bottom: 1px solid var(--border-color-light);
  background: linear-gradient(180deg, var(--bg-soft) 0%, var(--bg-light) 100%);
}

.el-dialog__title {
  font-size: 17px !important;
  font-weight: 600 !important;
  color: var(--heading-color) !important;
  letter-spacing: -0.2px;
}

.el-dialog__body {
  padding: 28px !important;
  color: var(--text-color-primary);
}

.el-dialog__footer {
  border-top: 1px solid var(--border-color-light);
  padding: 18px 28px !important;
  background: var(--bg-soft);
}

/* Tabs */
.el-tabs__nav-wrap::after {
  background-color: var(--border-color-light) !important;
  height: 1px !important;
}

.el-tabs__active-bar {
  background: var(--gradient-primary) !important;
  height: 3px !important;
  border-radius: 2px;
}

.el-tabs__item {
  font-size: 15px !important;
  font-weight: 500 !important;
  color: var(--text-color-secondary) !important;
  height: 44px !important;
  line-height: 44px !important;
  transition: var(--transition-fast) !important;
}

.el-tabs__item.is-active,
.el-tabs__item:hover {
  color: var(--primary-color) !important;
}

/* Table */
.el-table {
  border-radius: var(--radius-md) !important;
  overflow: hidden;
  border: 1px solid var(--border-color-light) !important;
}

.el-table th.el-table__cell {
  background-color: var(--bg-soft) !important;
  color: var(--heading-color) !important;
  font-weight: 600 !important;
  border-bottom: 1px solid var(--border-color-light) !important;
}

.el-table td.el-table__cell {
  border-bottom: 1px solid var(--border-color-light) !important;
}

.el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell {
  background-color: var(--primary-50) !important;
}

/* Form */
.el-form-item__label {
  font-weight: 500;
  color: var(--text-color-primary) !important;
}

/* Descriptions */
.el-descriptions__body {
  background: var(--bg-light);
}

.el-descriptions__header {
  margin-bottom: 16px !important;
}

.el-descriptions-item__label.is-bordered-label {
  background: var(--bg-soft) !important;
  color: var(--text-color-secondary) !important;
  font-weight: 500 !important;
}

/* Backtop */
.el-backtop {
  background: var(--gradient-primary) !important;
  color: #fff !important;
  box-shadow: var(--shadow-lg) !important;
  width: 44px !important;
  height: 44px !important;
}

.el-backtop:hover {
  background: linear-gradient(135deg, #2E75D9 0%, #4493EE 100%) !important;
  transform: translateY(-2px);
}

/* 卡片 - el-card */
.el-card {
  border-radius: var(--radius-lg) !important;
  border: 1px solid var(--border-color-light) !important;
  transition: var(--transition-base);
}

.el-card.is-hover-shadow:hover,
.el-card.is-always-shadow {
  box-shadow: var(--shadow-lg) !important;
}

/* 高亮文本 */
.highlight {
  background: linear-gradient(120deg, rgba(255, 234, 0, 0.45) 0%, rgba(255, 234, 0, 0.45) 100%);
  background-repeat: no-repeat;
  background-size: 100% 50%;
  background-position: 0 88%;
  padding: 0 2px;
  font-weight: 600;
  color: var(--heading-color);
}

/* 全局过渡动画 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* 容器 */
.container {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
}

@media screen and (max-width: 992px) {
  html, body {
    font-size: 14px;
  }
}

@media screen and (max-width: 768px) {
  html, body {
    font-size: 13px;
  }

  .container {
    padding: 0 16px;
  }
}
</style>
